/** OpenRouter 配置弹窗：复用现有集成弹窗，Key 仅录入，显示凭据尾号。 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useOpenRouterCredential } from '~/data/queries';
import { openrouterApi } from '~/hooks/use-openrouter';
import { ConfirmDialog, Dialog } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { PasswordInput } from '~/ui/PasswordInput';
import { toast } from '~/ui/Toast';
import { ProviderConcurrencyField } from './ProviderConcurrencyField';

function ConfigForm({ configured }: { configured: boolean }) {
  const [apiKey, setApiKey] = useState('');
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: () =>
      openrouterApi.upsertKey({
        ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
      }),
    onSuccess: async () => {
      setApiKey('');
      await qc.invalidateQueries({ queryKey: ['integrations', 'openrouter'] });
      toast('OpenRouter 配置已保存', { tone: 'pos' });
    },
  });
  return (
    <>
      <FormRow
        label="API Key"
        required={!configured}
        hint={configured ? '留空保留当前 Key；输入新 Key 可更换' : 'OpenRouter → Keys 获取'}
      >
        <PasswordInput
          className="input mono"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="输入 OpenRouter API Key"
          autoComplete="new-password"
        />
      </FormRow>
      <p className="muted">Key 加密存储，明文不会回显。模型与提示词在「大模型配置」中设置。</p>
      {save.error && (
        <div role="alert" style={{ color: 'var(--neg)' }}>
          {save.error.message}
        </div>
      )}
      <button
        className="btn btn-accent"
        disabled={save.isPending || (!configured && apiKey.trim().length < 8)}
        onClick={() => save.mutate()}
      >
        {save.isPending ? '保存中…' : '保存配置'}
      </button>
    </>
  );
}

export function OpenRouterConfigDialog({ onClose }: { onClose: () => void }) {
  const q = useOpenRouterCredential();
  const qc = useQueryClient();
  const [removing, setRemoving] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['integrations', 'openrouter'] });
  const test = useMutation({
    mutationFn: openrouterApi.testConnection,
    onSuccess: () => toast('OpenRouter Key 验证通过', { tone: 'pos' }),
    onError: (e) => toast(e.message, { tone: 'neg' }),
  });
  const remove = useMutation({
    mutationFn: openrouterApi.removeKey,
    onSuccess: async () => {
      await refresh();
      setRemoving(false);
      toast('OpenRouter 配置已移除', { tone: 'warn' });
    },
    onError: (e) => toast(e.message, { tone: 'neg' }),
  });
  return (
    <Dialog
      title="OpenRouter 集成配置"
      subtitle="全局模型服务 · 落地页 AI 审核与脱敏"
      icon="IconBranch"
      width={620}
      onClose={onClose}
      closeOnEsc={!removing}
      footer={
        <button className="btn" onClick={onClose}>
          完成
        </button>
      }
    >
      <div style={{ padding: 18 }}>
        {q.isPending ? (
          <p>加载配置…</p>
        ) : q.isError ? (
          <div role="alert">{q.error.message}</div>
        ) : (
          <>
            <p>
              {q.data.configured ? `已配置 · Key 尾号 ${q.data.key_tail}` : '尚未配置 OpenRouter'}
            </p>
            <ConfigForm key={q.data.updated_at ?? 'unset'} configured={q.data.configured} />
            {q.data.configured && (
              <div className="hstack" style={{ gap: 8, marginTop: 16 }}>
                <button className="btn" disabled={test.isPending} onClick={() => test.mutate()}>
                  {test.isPending ? '测试中…' : '测试连接'}
                </button>
                <button className="btn btn-danger" onClick={() => setRemoving(true)}>
                  移除配置
                </button>
              </div>
            )}
          </>
        )}
        <ProviderConcurrencyField provider="openrouter" label="OpenRouter" />
      </div>
      {removing && (
        <ConfirmDialog
          title="移除 OpenRouter 配置"
          message="移除后使用 OpenRouter 的审核和脱敏将无法执行；既有任务产物不受影响。"
          requireTyping="OpenRouter"
          tone="danger"
          onConfirm={() => remove.mutate()}
          onClose={() => setRemoving(false)}
        />
      )}
    </Dialog>
  );
}
